import { useEffect } from 'react';

export default function useReadingChapters(containerRef, content) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !content) return;
    const chapters = Array.from(container.querySelectorAll('.bible-reading-chapter'));
    if (!chapters.length) return;
    let frame;
    let measurementFrame;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(measurementFrame);
      // ResizeObserver callbacks must not change layout during delivery.
      // Defer even the removal of content-visibility to the next frame.
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        chapters.forEach((chapter) => chapter.classList.remove('bible-reading-chapter--measured'));
        measurementFrame = requestAnimationFrame(() => {
          if (disposed) return;
          // Read all heights before writing any styles.
          const heights = chapters.map((chapter) => chapter.getBoundingClientRect().height);
          chapters.forEach((chapter, index) => {
            chapter.style.containIntrinsicBlockSize = `auto ${heights[index]}px`;
            chapter.classList.add('bible-reading-chapter--measured');
          });
        });
      });
    };
    measure();
    // Always compare content-box widths, not an initial border-box width.
    let width;
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver((entries) => {
      const nextWidth = entries[0].contentRect.width;
      if (nextWidth > 0 && (width === undefined || Math.abs(nextWidth - width) >= 0.5)) {
        width = nextWidth;
        measure();
      }
    });
    resize?.observe(container);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        target.classList.toggle('bible-reading-chapter--nearby', isIntersecting);
      });
    }, { rootMargin: '800px 0px' });
    chapters.forEach((chapter) => observer?.observe(chapter));
    const fontsChanged = () => { if (!disposed) measure(); };
    document.fonts?.addEventListener('loadingdone', fontsChanged);
    window.addEventListener('resize', measure);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(measurementFrame);
      resize?.disconnect();
      observer?.disconnect();
      document.fonts?.removeEventListener('loadingdone', fontsChanged);
      window.removeEventListener('resize', measure);
      chapters.forEach((chapter) => {
        chapter.classList.remove('bible-reading-chapter--measured', 'bible-reading-chapter--nearby');
        chapter.style.removeProperty('contain-intrinsic-block-size');
      });
    };
  }, [containerRef, content]);
}
